import React, { useMemo, useContext } from 'react'
import { GridColumnMenuProps, GridApi, GridSortDirection, GridColDef } from '@mui/x-data-grid-pro'
import styled from 'particles/themes/styled'
import { useTranslation } from 'react-i18next'
import { OptionsState } from 'particles/Dropdown'
import {
    isAToZField,
    isHighestToLowestField,
    isSortable,
    isLatestToOldestField,
    isOppositeSortField,
} from 'components/utils'
import { FIELDS_TYPE } from 'components/utils/filterAndSort'
import { SORT_DIRECTION, getSubFieldValues } from 'components/utils/filterAndSort'
import { useSelector } from 'react-redux'
import { selectSortItem } from 'state/review'
import { TableContext, SortItem } from 'components/Shared/MuiTable/PartiTable'
import MenuItem from 'particles/MenuItem'

interface GridColDefProps extends GridColDef {
    subFields?: {
        field: FIELDS_TYPE
        headerName: string
    }[]
}

interface ColumnMenuProps extends Omit<GridColumnMenuProps, 'currentColumn'> {
    currentColumn: GridColDefProps
    className?: string
    apiRef: React.MutableRefObject<GridApi>
    onAddFilterField: (option: OptionsState) => void
    onAddSortField: (option: SortItem, hideTooltip?: boolean) => void
    visibleModel: Record<string, boolean>
    setVisibleModel: (newModel: Record<string, boolean>) => void
    isFilterable: (field: string) => boolean
}
interface FilterAndSortButtonContextProps {
    filterable: boolean
    sortable: boolean
    ascendingText: string
    descendingText: string
}

const ColumnMenu: React.FC<ColumnMenuProps> = ({
    currentColumn,
    apiRef,
    className,
    onAddFilterField,
    onAddSortField,
    visibleModel,
    setVisibleModel,
    isFilterable,
}) => {
    const [t] = useTranslation()
    const { showFilter, showSort } = useContext(TableContext)
    const subFieldValues = getSubFieldValues(t)

    const sortItem = useSelector(selectSortItem)

    const sortField = sortItem?.field
    const sortValue = sortItem?.sort

    const handleHideColumnMenu = () => {
        apiRef.current.hideColumnMenu()
    }

    const hideColumnOnClick = () => {
        handleHideColumnMenu()
        setVisibleModel({ ...visibleModel, [currentColumn?.field]: false })
    }

    const handleAddFilterField = (subField?: string) => {
        handleHideColumnMenu()
        onAddFilterField({
            label: subField ? subFieldValues[subField].headerName || '' : currentColumn.headerName || '',
            value: subField ? subField : currentColumn.field,
        })
    }

    const handleSelectSort = (sortDirection: GridSortDirection, sortDirectionLabel: string, subField?: string) => {
        handleHideColumnMenu()

        onAddSortField(
            {
                field: subField ? subField : currentColumn.field,
                sort: sortDirection,
                fieldLabel: subField ? subFieldValues[subField].headerName || '' : currentColumn.headerName || '',
                sortDirectionLabel,
            },
            true,
        )
    }
    const isAToZSort = isAToZField(currentColumn.field)
    const isHighestToLowest = isHighestToLowestField(currentColumn.field)
    const isLatestToOldest = isLatestToOldestField(currentColumn.field)
    const isOppositeSort = isOppositeSortField(currentColumn.field)

    const FilterAndSortButtonContext: FilterAndSortButtonContextProps = useMemo(() => {
        let context = {
            filterable: isFilterable(currentColumn.field),
            sortable: isSortable(currentColumn.field),
            ascendingText: '',
            descendingText: '',
        }

        if (isAToZSort) {
            context = {
                ...context,
                ascendingText: t('button.sortAscendingAlphabet'),
                descendingText: t('button.sortDescendingAlphabet'),
            }
        } else if (isHighestToLowest) {
            context = {
                ...context,
                ascendingText: t('button.sortHighestLowest'),
                descendingText: t('button.sortLowestHighest'),
            }
        } else if (isLatestToOldest) {
            context = {
                ...context,
                ascendingText: t('button.sortLatestOldest'),
                descendingText: t('button.sortOldestLatest'),
            }
        }
        if (currentColumn.field === FIELDS_TYPE.HIGHLIGHT) {
            context = {
                ...context,
                ascendingText: t('button.sortSavedToNotSaved'),
                descendingText: t('button.sortNotSavedToSaved'),
            }
        }
        return context
    }, [currentColumn.field])

    // Define the subfield sort configurations for different field types
    const subFieldSortConfig = {
        aToZ: {
            direction: SORT_DIRECTION.ASC,
            label: t('button.sortAscendingAlphabet'),
        },
        highestToLowest: {
            direction: SORT_DIRECTION.DESC,
            label: t('button.sortHighestLowest'),
        },
        latestToOldest: {
            direction: SORT_DIRECTION.ASC,
            label: t('button.sortLatestOldest'),
        },
    }

    // Helper function to get sort config based on field type
    const getSubFieldSortConfig = (field: FIELDS_TYPE) => {
        if (isAToZField(field)) return subFieldSortConfig.aToZ
        if (isHighestToLowestField(field)) return subFieldSortConfig.highestToLowest
        if (isLatestToOldestField(field)) return subFieldSortConfig.latestToOldest
        return null
    }

    const handleSubFieldSort = (subField: FIELDS_TYPE) => {
        const config = getSubFieldSortConfig(subField)
        if (!config) return

        return handleSelectSort(config.direction, config.label, subField)
    }

    return (
        <div className={className} id="container">
            {showSort &&
                FilterAndSortButtonContext.sortable &&
                (currentColumn?.subFields?.length ? (
                    <>
                        {currentColumn?.subFields?.length && (
                            <MenuItem.Title key="title" text={t('dropdownMenu.sortByHeader').toUpperCase()} />
                        )}
                        {currentColumn.subFields.map(item => (
                            <MenuItem.Text
                                key={item.field}
                                size="s"
                                icon="arrowsDownUp"
                                text={item.headerName}
                                selected={sortField === item.field}
                                onClick={() => handleSubFieldSort(item.field)}
                            />
                        ))}

                        <MenuItem.Divider />
                    </>
                ) : (
                    <>
                        <MenuItem.Text
                            size="s"
                            icon="sortAscending"
                            text={FilterAndSortButtonContext.ascendingText}
                            onClick={() =>
                                handleSelectSort(
                                    isOppositeSort ? SORT_DIRECTION.DESC : SORT_DIRECTION.ASC,
                                    FilterAndSortButtonContext.ascendingText,
                                )
                            }
                            selected={
                                isOppositeSort
                                    ? sortField === currentColumn.field && sortValue === SORT_DIRECTION.DESC
                                    : sortField === currentColumn.field && sortValue === SORT_DIRECTION.ASC
                            }
                        />
                        <MenuItem.Text
                            size="s"
                            icon="sortDescending"
                            text={FilterAndSortButtonContext.descendingText}
                            onClick={() =>
                                handleSelectSort(
                                    isOppositeSort ? SORT_DIRECTION.ASC : SORT_DIRECTION.DESC,
                                    FilterAndSortButtonContext.descendingText,
                                )
                            }
                            selected={
                                isOppositeSort
                                    ? sortField === currentColumn.field && sortValue === SORT_DIRECTION.ASC
                                    : sortField === currentColumn.field && sortValue === SORT_DIRECTION.DESC
                            }
                        />
                        <MenuItem.Divider />
                    </>
                ))}
            {showFilter && currentColumn.subFields?.length ? (
                <>
                    {currentColumn?.subFields?.length && (
                        <MenuItem.Title key="title" text={t('dropdownMenu.filterByHeader').toUpperCase()} />
                    )}
                    {currentColumn.subFields
                        .filter(item => isFilterable(item.field))
                        .map(item => (
                            <MenuItem.Text
                                key={item.field}
                                icon="funnel"
                                text={item.headerName}
                                onClick={() => handleAddFilterField(item.field)}
                            />
                        ))}
                </>
            ) : (
                <>
                    {showFilter && FilterAndSortButtonContext.filterable && (
                        <>
                            <MenuItem.Text
                                icon="funnel"
                                text={t('button.filter')}
                                onClick={() => handleAddFilterField()}
                            />
                            <MenuItem.Divider />
                        </>
                    )}
                </>
            )}
            <MenuItem.Text icon="eyeClosed" text={t('button.hideColumn')} onClick={hideColumnOnClick} />
        </div>
    )
}

// Small Menu style from particles/Menu
export default styled(ColumnMenu)`
    display: flex;
    width: 216px;
    min-width: 100px;
    flex-direction: column;
    align-items: flex-start;
    gap: ${({ theme }) => theme.spacing[100]};
    border-radius: 4px;
    border: 8px solid ${({ theme }) => theme.colors.surface.primary};
    background: ${({ theme }) => theme.colors.surface.primary};
    box-shadow: ${({ theme }) => theme.shadows.level3};
`
